import {isEqual} from 'lodash'
import {
  DragAndDropList,
  Draggable,
  DragHandle,
  MOVE_AFTER,
  MOVE_BEFORE,
} from '../../common/components/DragAndDrop.js'
import ConfirmModal from '../../common/components/Modal/ConfirmModal.js'
import {saveUserSettings, userSettingSelector} from '../../data/me.js'

import {
  formsSelector,
  getState,
  onlyIfForm,
  removeForm,
  setForm,
  updateForm,
} from '../../store.js'
import {showMessageToast} from '../Header/Toast/index.js'
import Icon from '../../common/icons/Icon.js'

const MODAL_FORM = 'filter_settings_modal'

export function showFilterSettingsModal({title, userSettingsKey, listOptions}) {
  const list = filterOrderSelector(getState(), {userSettingsKey, listOptions})

  setForm(MODAL_FORM, {
    title,
    userSettingsKey,
    list: [...list],
    listOptions,
    listLookup: listOptions.reduce((prev, option) => {
      prev[option.value] = option

      return prev
    }, {}),
    serverError: null,
    isSaving: false,
  })
}

export function filterOrderSelector(state, {userSettingsKey, listOptions}) {
  const defaultList = listOptions.map(({value}) => value)

  let list = userSettingSelector(state, {
    setting: userSettingsKey,
    defaultValue: defaultList,
  })

  // remove any items that have been removed from the default list
  list = list.filter((item) => defaultList.includes(item))

  // if the list aren't the same size then there are new items in the default list
  // that need to be added to the list
  if (list.length !== defaultList.length) {
    const newItems = defaultList.filter((item) => !list.includes(item))

    list.push(...newItems)
  }

  return list
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

async function onFilterMove(values, nearValue, where) {
  const {list: originalList} = modalFormSelector(getState())

  // get sorted items and exclude moving item
  const list = originalList.filter((item) => !values.includes(item))

  // get index of near item
  const nearIndex = list.indexOf(nearValue)

  // put moving item in it's place
  if (where === MOVE_BEFORE) {
    list.splice(nearIndex, 0, ...values)
  } else if (where === MOVE_AFTER) {
    list.splice(nearIndex + 1, 0, ...values)
  }

  if (!isEqual(list, originalList)) {
    updateModalForm({list})
  }
}

async function applyList() {
  try {
    const {list, userSettingsKey, listOptions} = modalFormSelector(getState())
    const originalList = filterOrderSelector(getState(), {
      userSettingsKey,
      listOptions,
    })

    if (!isEqual(list, originalList)) {
      updateModalForm({serverError: null, isSaving: true})

      await saveUserSettings({[userSettingsKey]: list})
    }

    showMessageToast('Filter settings were updated.')

    closeModal()
  } catch (err) {
    updateModalForm({
      serverError: err.message || err.error_message,
      isSaving: false,
    })
  }
}

function FilterSettingsModal({form}) {
  return (
    <ConfirmModal
      title={form.title}
      modalSize="sm"
      onConfirm={() => applyList()}
      onCancel={() => closeModal()}
      confirmText="Apply"
      cancelText="Cancel"
      error={form.serverError}
      isSaving={form.isSaving}
    >
      <DragAndDropList onMove={onFilterMove}>
        <p className="fs-01 lh-md margin-bottom-25">
          <strong className="inline-block v-align-middle">
            Reorder your filters by dragging the{' '}
            <span className="inline-block margin-left-3 margin-right-3 v-align-middle op-30">
              <Icon
                name="drag-handle"
                className="i-drag-handle block fs-03-04 lh-sm"
              />
            </span>{' '}
            icon.
          </strong>
        </p>
        <div className="wrap--modal-draggable-items">
          {form.list.map((item, index) => (
            <Draggable
              className="flex wrap--draggable-item"
              key={item}
              id={item}
              uiIndex={index}
            >
              <DragHandle className="padding-top-10 padding-bottom-10 border-top--light" />
              <div className="fs-01 flex--justify-col border-top--light padding-top-10 padding-bottom-10 flex-grow">
                {form.listLookup[item].display}
              </div>
            </Draggable>
          ))}
        </div>
      </DragAndDropList>
    </ConfirmModal>
  )
}

export default onlyIfForm(FilterSettingsModal, modalFormSelector)
